import { Link } from "react-router-dom";
import { Clock, Phone, Mail, MapPin, MessageCircle, Instagram, Facebook, ArrowRight, Scissors } from "lucide-react";
import { mediaUrl, fmtPrice, DAYS, fmtDate } from "../api";
import { BookingButton } from "./Booking";

export function Img({ id, alt = "", className = "", fallback = null }) {
  const src = mediaUrl(id);
  if (!src) return fallback ?? <div className={`bg-gradient-to-br from-[var(--brand-primary)] via-[var(--brand-primary-soft)] to-[var(--brand-primary-dark)] ${className}`} aria-hidden="true" />;
  return <img src={src} alt={alt} loading="lazy" className={`object-cover ${className}`} />;
}

export function Eyebrow({ children }) {
  return <p className="text-[11px] font-semibold uppercase tracking-[0.3em] text-[var(--brand-accent)]">{children}</p>;
}

export function SectionHeading({ eyebrow, title, text, light = false, action }) {
  return (
    <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-10 sm:mb-14">
      <div className="max-w-2xl">
        {eyebrow && <Eyebrow>{eyebrow}</Eyebrow>}
        <h2 className={`font-serif text-3xl sm:text-4xl lg:text-5xl font-semibold tracking-tight mt-3 ${light ? "text-stone-50" : "text-stone-900"}`}>{title}</h2>
        {text && <p className={`mt-4 text-base leading-relaxed ${light ? "text-stone-300" : "text-stone-600"}`}>{text}</p>}
      </div>
      {action}
    </div>
  );
}

export function LinkArrow({ to, children, light = false, testid }) {
  return (
    <Link to={to} data-testid={testid} className={`group inline-flex items-center gap-2 text-sm font-semibold tracking-wide ${light ? "text-[var(--brand-accent-light)]" : "text-[var(--brand-primary)]"}`}>
      {children} <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
    </Link>
  );
}

export function PriceTag({ price, prefix, className = "" }) {
  const p = fmtPrice(price);
  return <span className={`font-serif text-xl text-[var(--brand-primary)] whitespace-nowrap ${className}`}>{p ? `${prefix ? prefix + " " : ""}${p}` : "auf Anfrage"}</span>;
}

export function ServiceCard({ s, compact = false }) {
  return (
    <article data-testid={`service-card-${s.id}`} className="group rounded-2xl bg-white border border-stone-200/70 overflow-hidden shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-[transform,box-shadow] duration-300">
      <Img id={s.image_media_id} alt={s.name} className={`w-full ${compact ? "h-40" : "h-52"}`}
        fallback={<div className={`w-full ${compact ? "h-40" : "h-52"} bg-[#F1EAE0] flex items-center justify-center`}><Scissors className="h-8 w-8 text-[var(--brand-accent)]" /></div>} />
      <div className="p-5">
        <div className="flex items-start justify-between gap-3">
          <h3 className="font-serif text-xl text-stone-900 leading-tight">{s.name}</h3>
          <PriceTag price={s.price} prefix={s.price_prefix} />
        </div>
        {s.description && <p className="text-sm text-stone-600 mt-2 leading-relaxed">{s.description}</p>}
        {s.duration_minutes ? <p className="inline-flex items-center gap-1.5 text-xs text-stone-500 mt-3"><Clock className="h-3.5 w-3.5" /> ca. {s.duration_minutes} Min.</p> : null}
        <div className="mt-4"><BookingButton serviceId={s.id} variant="outline" label="Anfragen" className="!px-4 !py-1.5 !text-xs" testid={`service-book-${s.id}`} /></div>
      </div>
    </article>
  );
}

export function PromoCard({ p, dark = false }) {
  const op = fmtPrice(p.original_price);
  const pp = fmtPrice(p.promo_price);
  return (
    <article data-testid={`promo-card-${p.id}`} className={`rounded-2xl overflow-hidden border shadow-sm ${dark ? "bg-[var(--brand-primary-dark)] border-amber-500/10" : "bg-white border-stone-200/70"}`}>
      <Img id={p.image_media_id} alt={p.title} className="w-full h-48" />
      <div className="p-6">
        <div className="flex items-center justify-between gap-3">
          <span className="text-[10px] font-semibold uppercase tracking-[0.25em] text-[var(--brand-accent)]">Angebot</span>
          {(p.start_date || p.end_date) && (
            <span className={`text-xs ${dark ? "text-stone-300" : "text-stone-500"}`}>
              {p.start_date ? fmtDate(p.start_date) : ""}{p.start_date && p.end_date ? " – " : ""}{p.end_date ? `bis ${fmtDate(p.end_date)}` : ""}
            </span>
          )}
        </div>
        <h3 className={`font-serif text-2xl mt-2 ${dark ? "text-stone-50" : "text-stone-900"}`}>{p.title}</h3>
        {p.description && <p className={`text-sm mt-2 leading-relaxed ${dark ? "text-stone-300" : "text-stone-600"}`}>{p.description}</p>}
        {(op || pp) && (
          <div className="flex items-baseline gap-3 mt-4">
            {pp && <span className={`font-serif text-2xl ${dark ? "text-[var(--brand-accent-light)]" : "text-[var(--brand-primary)]"}`}>{pp}</span>}
            {op && <span className={`text-sm line-through ${dark ? "text-stone-400" : "text-stone-400"}`}>{op}</span>}
          </div>
        )}
      </div>
    </article>
  );
}

export function HoursTable({ hours, special = [], light = false }) {
  const todayIdx = (new Date().getDay() + 6) % 7;
  const base = light ? "text-stone-200" : "text-stone-700";
  if (!hours?.published) {
    return <p data-testid="hours-unpublished" className={`text-sm ${light ? "text-stone-300" : "text-stone-600"}`}>Unsere Öffnungszeiten werden in Kürze veröffentlicht. Bitte kontaktieren Sie uns für aktuelle Zeiten.</p>;
  }
  const days = [...(hours.days || [])].sort((a, b) => a.day - b.day);
  return (
    <div data-testid="hours-table">
      <ul className="divide-y divide-stone-200/30">
        {days.map((d) => (
          <li key={d.day} data-testid={`hours-day-${d.day}`} className={`flex justify-between gap-4 py-2.5 text-sm ${base} ${d.day === todayIdx ? "font-semibold" : ""}`}>
            <span>{DAYS[d.day]}{d.day === todayIdx && <span className="ml-2 text-[10px] uppercase tracking-widest text-[var(--brand-accent)]">Heute</span>}</span>
            <span className="text-right">
              {d.closed ? "Geschlossen" : (
                d.breaks?.length ? [[d.open, d.breaks[0].start], ...d.breaks.map((b, i) => [b.end, d.breaks[i + 1]?.start || d.close])].map((r) => r.join("–")).join(", ") : `${d.open}–${d.close}`
              )}
            </span>
          </li>
        ))}
      </ul>
      {special.length > 0 && (
        <div className="mt-5" data-testid="special-hours">
          <p className="text-[11px] font-semibold uppercase tracking-[0.25em] text-[var(--brand-accent)]">Sonderzeiten</p>
          <ul className="mt-2 space-y-1.5">
            {special.map((s) => (
              <li key={s.id} className={`flex justify-between gap-4 text-sm ${base}`}>
                <span>{fmtDate(s.date)}{s.end_date && s.end_date !== s.date ? ` – ${fmtDate(s.end_date)}` : ""}{s.label ? ` · ${s.label}` : ""}</span>
                <span>{s.closed ? "Geschlossen" : `${s.open}–${s.close}`}</span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {hours.note && <p className={`text-xs mt-4 ${light ? "text-stone-400" : "text-stone-500"}`}>{hours.note}</p>}
    </div>
  );
}

export function ContactList({ b, light = false }) {
  const cls = `flex items-start gap-3 text-sm ${light ? "text-stone-200" : "text-stone-700"}`;
  const address = [b.street, [b.postal_code, b.city].filter(Boolean).join(" "), b.country].filter(Boolean);
  const wa = (b.whatsapp || "").replace(/[^\d+]/g, "").replace(/^\+/, "");
  return (
    <ul className="space-y-3" data-testid="contact-list">
      {address.length > 0 && <li className={cls}><MapPin className="h-4 w-4 mt-0.5 text-[var(--brand-accent)] shrink-0" /><span>{address.map((l, i) => <span key={i} className="block">{l}</span>)}</span></li>}
      {b.phone && <li className={cls}><Phone className="h-4 w-4 mt-0.5 text-[var(--brand-accent)] shrink-0" /><a href={`tel:${b.phone.replace(/\s/g, "")}`} className="hover:underline">{b.phone}</a></li>}
      {b.whatsapp && <li className={cls}><MessageCircle className="h-4 w-4 mt-0.5 text-[var(--brand-accent)] shrink-0" /><a href={`https://wa.me/${wa}`} target="_blank" rel="noreferrer" className="hover:underline">WhatsApp: {b.whatsapp}</a></li>}
      {b.email && <li className={cls}><Mail className="h-4 w-4 mt-0.5 text-[var(--brand-accent)] shrink-0" /><a href={`mailto:${b.email}`} className="hover:underline">{b.email}</a></li>}
      {(b.instagram || b.facebook || b.tiktok || b.other_link_url) && (
        <li className="flex items-center gap-3 pt-1">
          {b.instagram && <a href={b.instagram} target="_blank" rel="noreferrer" aria-label="Instagram" className="p-2 rounded-full border border-[var(--brand-accent-40)] text-[var(--brand-accent)] hover:bg-[var(--brand-accent-10)]"><Instagram className="h-4 w-4" /></a>}
          {b.facebook && <a href={b.facebook} target="_blank" rel="noreferrer" aria-label="Facebook" className="p-2 rounded-full border border-[var(--brand-accent-40)] text-[var(--brand-accent)] hover:bg-[var(--brand-accent-10)]"><Facebook className="h-4 w-4" /></a>}
          {b.tiktok && <a href={b.tiktok} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full border border-[var(--brand-accent-40)] text-[var(--brand-accent)] text-xs font-semibold hover:bg-[var(--brand-accent-10)]">TikTok</a>}
          {b.other_link_url && <a href={b.other_link_url} target="_blank" rel="noreferrer" className="px-3 py-1.5 rounded-full border border-[var(--brand-accent-40)] text-[var(--brand-accent)] text-xs font-semibold hover:bg-[var(--brand-accent-10)]">{b.other_link_label || "Link"}</a>}
        </li>
      )}
    </ul>
  );
}

export function PageHero({ eyebrow, title, text, action }) {
  return (
    <section className="bg-[var(--brand-primary)] text-stone-50 relative overflow-hidden">
      <div className="absolute -right-24 -top-24 h-72 w-72 rounded-full bg-[var(--brand-accent-10)] blur-3xl" />
      <div className="max-w-6xl mx-auto px-5 py-16 sm:py-24">
        <Eyebrow>{eyebrow}</Eyebrow>
        <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl font-semibold tracking-tight mt-3">{title}</h1>
        {text && <p className="text-base text-stone-300 mt-5 max-w-xl leading-relaxed">{text}</p>}
        {action && <div className="mt-8">{action}</div>}
      </div>
    </section>
  );
}

export function Empty({ children }) {
  return <p className="text-sm text-stone-500 italic" data-testid="empty-state">{children}</p>;
}
